import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  ContractNature,
  Currency,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { useSub } from "../../../../../common/subscription/useSub";
import { CONTRACT_NATURE_OPTIONS } from "../utils/publicProcurementOptions";
import { foaPartBanner, isOutsideFoa } from "../utils/foaPartDisplay";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { resolveProcess, useStepLabel, type OrgWizardAccess } from "../wizardSteps";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  nextIsAction?: boolean;
  isNextLoading?: boolean;
}

export const InfoStep = ({ eventId, previousStep, nextStep, nextStepLabel, nextIsAction, isNextLoading }: Props) => {
  const { t } = useTranslation();
  const sub = useSub();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();

  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const title = state.eventData.title;
  const estimatedValue = state.eventContext.estimatedValue?.value ?? null;
  const contractNature = state.eventContext.contractNature ?? "";
  const foaPart = publicContext?.foaPart ?? null;
  const resolvesToPrivate =
    !sub.hasSourcingEventV2Access || isOutsideFoa(estimatedValue != null ? Number(estimatedValue) : null);
  const banner = resolvesToPrivate ? null : foaPartBanner(foaPart, estimatedValue);

  const stepLabel = useStepLabel();
  const access: OrgWizardAccess = {
    hasPublicProcurementAccess: sub.hasSourcingEventV2Access,
    hasProductAccess: sub.hasProductAccess,
  };
  const processUnresolved = resolveProcess(state.eventContext.type, access) === null;
  const predictedProcess = resolveProcess(resolvesToPrivate ? "Private" : "Public", access);
  const comingSteps = (predictedProcess?.steps ?? [])
    .slice(1)
    .map((step) => stepLabel(step.key))
    .join(" · ");

  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";
  const titleFieldError = fieldError("eventData.title");
  const estimatedValueFieldError = fieldError("eventContext.estimatedValue.value");

  return (
    <Box pb="8" pt="10">
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <FormLabel fontWeight="bold" fontSize="md" mb="4">
          {t("General information")}
        </FormLabel>

        <FormControl isRequired mb="2" isInvalid={!!titleFieldError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Title")}</FormLabel>
            {isSaving("eventData.title") && <Spinner size="xs" />}
          </Flex>
          <Input
            type="text"
            value={title}
            placeholder={t("E.g. Office supplies for municipality of Oslo") ?? undefined}
            onChange={(e) => updateField({ eventData: { title: e.target.value } }, { debounceMs: 300 })}
          />
          {titleFieldError ? (
            <FormErrorMessage>{formatFieldError(titleFieldError)}</FormErrorMessage>
          ) : (
            <FormHelperText>{t("A short, descriptive title for the procurement")}</FormHelperText>
          )}
        </FormControl>
      </Box>

      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8" bg="smBackground">
        <FormLabel fontWeight="bold" fontSize="md" mb="4">
          {t("Classification and value")}
        </FormLabel>

        <FormControl mb="6" isInvalid={!!estimatedValueFieldError}>
          <FormLabel>
            {t("Estimated value")} {isSaving("eventContext.estimatedValue") && <Spinner size="xs" />}
          </FormLabel>
          <InputGroup>
            <NumericInput
              numericValue={estimatedValue ? Number(estimatedValue) : null}
              placeholder="0"
              onNumericChange={(value) =>
                updateField(
                  { eventContext: { estimatedValue: { value, currency: Currency.Nok } } },
                  { debounceMs: 300 },
                )
              }
            />
            <InputRightAddon>{t("NOK")}</InputRightAddon>
          </InputGroup>
          {estimatedValueFieldError && (
            <FormErrorMessage>{formatFieldError(estimatedValueFieldError)}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl>
          <FormLabel>
            {t("Contract type")} {isSaving("eventContext.contractNature") && <Spinner size="xs" />}
          </FormLabel>
          <RadioOptionGroup
            options={CONTRACT_NATURE_OPTIONS}
            value={contractNature}
            allowDeselect
            onChange={(value) =>
              updateField({ eventContext: { contractNature: value ? (value as ContractNature) : null } })
            }
          />
        </FormControl>

        {resolvesToPrivate && !estimatedValueFieldError ? (
          <Alert status="info" mt="4" borderRadius="md" fontSize="sm">
            <AlertIcon />
            <Box>
              {sub.hasSourcingEventV2Access
                ? t("Below 500 000 NOK — the procurement continues as a private procurement")
                : t("The procurement runs as a private procurement")}
              {processUnresolved && (
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Next steps")}: {comingSteps}
                </Text>
              )}
            </Box>
          </Alert>
        ) : (
          <Box mt="4" fontSize="sm" color="smMuted">
            <Text>{t("The procurement runs as a public procurement")}</Text>
            {processUnresolved && (
              <Text fontSize="xs" mt="1">
                {t("Next steps")}: {comingSteps}
              </Text>
            )}
          </Box>
        )}

        {banner && !estimatedValueFieldError && (
          <Alert status={banner.status} mt="4" borderRadius="md" fontSize="sm">
            <AlertIcon />
            {t(banner.messageKey)}
          </Alert>
        )}
      </Box>

      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
        nextIsAction={nextIsAction}
        isNextLoading={isNextLoading}
      />
    </Box>
  );
};
